/**
 * Budget Distribution Utilities
 * Functions for managing budget category distribution with default values.
 */

export interface CategoryPercentage {
  id: string;
  name: string;
  color: string;
  percentage: number;
  nameKey: string;
}

/**
 * Default budget distribution values.
 * Used when user has no saved settings or saved settings are invalid.
 */
export const DEFAULT_BUDGET_DISTRIBUTION: CategoryPercentage[] = [
  { id: 'custo-fixo', name: 'Custo Fixo', color: '#f97316', percentage: 30, nameKey: 'fixed_costs' },
  { id: 'liberdade', name: 'Liberdade Financeira', color: '#a855f7', percentage: 25, nameKey: 'financial_freedom' },
  { id: 'conforto', name: 'Conforto', color: '#3b82f6', percentage: 15, nameKey: 'comfort' },
  { id: 'metas', name: 'Metas', color: '#22c55e', percentage: 10, nameKey: 'goals' },
  { id: 'prazeres', name: 'Prazeres', color: '#eab308', percentage: 15, nameKey: 'pleasures' },
  { id: 'conhecimento', name: 'Conhecimento', color: '#06b6d4', percentage: 5, nameKey: 'knowledge' },
];

/**
 * Check if categories have valid (non-zero) percentages that sum to 100.
 * @param categories - Array of categories with percentage property
 * @returns true if percentages sum to exactly 100
 */
export function hasValidPercentages(categories: { percentage: number }[]): boolean {
  if (categories.length === 0) return false;
  const totalPercentage = categories.reduce((sum, cat) => sum + cat.percentage, 0);
  return totalPercentage === 100;
}

/**
 * Get initial budget distribution from preferences or defaults.
 * Priority: 1. Server preferences (if valid) 2. Store categories (if valid) 3. Default values
 *
 * @param preferencesCategories - Categories from server user preferences
 * @param storeCategories - Categories from local Zustand store
 * @returns Array of CategoryPercentage with valid percentages
 */
export function getInitialBudgetDistribution(
  preferencesCategories: { id: string; name: string; nameKey: string; color: string; percentage: number }[] | undefined,
  storeCategories: { id: string; name: string; color: string; percentage: number }[]
): CategoryPercentage[] {
  // First, try to use preferences from server (if valid)
  if (preferencesCategories && hasValidPercentages(preferencesCategories)) {
    return preferencesCategories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      color: cat.color,
      percentage: cat.percentage,
      nameKey: cat.nameKey,
    }));
  }

  // Second, try to use store categories (if valid)
  if (storeCategories.length > 0 && hasValidPercentages(storeCategories)) {
    return storeCategories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      color: cat.color,
      percentage: cat.percentage,
      nameKey: cat.id,
    }));
  }

  // Finally, use default values
  return DEFAULT_BUDGET_DISTRIBUTION;
}
